/**
 * The history rail's reads: the trainer's conversations, a page at a time, and the roster their
 * recipients are named from.
 *
 * ── One answer, when both reads have answered ────────────────────────────────────────────────
 * A row names its client, and the name comes from the roster by the client's id. The rail is handed
 * the list only once the history AND the roster have answered, so a row never stands as one thing
 * and becomes another. A roster that fails does not fail the rail: the conversations are still
 * listed, and a row whose client cannot be named says so.
 *
 * ── Kept current ─────────────────────────────────────────────────────────────────────────────
 * The open conversation moving on (a first message, a stage, a finished draft, a bind) changes its
 * own row, so the history is read again whenever that conversation's standing changes.
 */

import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  aiHistoryQueryOptions,
  historyEntryFromWire,
  historyGroups,
  invalidateAiHistory,
  type AiConversation,
} from '~/entities/ai-conversation';
import { rosterQueryOptions } from '~/entities/client';
import type { HistoryRailState } from '~/features/ai-chat';

interface BuddyHistoryInput {
  /**
   * Buddy is this trainer's, running or paused: the history is read only then. Until the status
   * says so the rail waits, so it never says "nothing here" before it knows.
   */
  readonly readable: boolean;
  /** The status read failed before it ever answered: the rail says so, with this retry. */
  readonly statusRetry: (() => void) | null;
  /** The conversation open beside the rail, once it is read. */
  readonly conversation: AiConversation | null;
}

export interface BuddyHistory {
  readonly state: HistoryRailState;
  readonly clientName: (clientId: number) => string | null;
  readonly now: Date;
  readonly timeZone: string;
  /** Read the history again: a conversation was deleted. */
  readonly refresh: () => void;
}

export function useBuddyHistory({ readable, statusRetry, conversation }: BuddyHistoryInput): BuddyHistory {
  const client = useQueryClient();
  const history = useInfiniteQuery({ ...aiHistoryQueryOptions(), enabled: readable });
  const roster = useQuery({ ...rosterQueryOptions(), enabled: readable });
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // The clock the groups and the moments are read against. It moves on every minute, so a rail
  // left open over midnight regroups, and „10:42" does not stand under „Dziś" tomorrow.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = window.setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => {
      window.clearInterval(tick);
    };
  }, []);

  // What of the open conversation shows in its row: when any of it changes, the row is stale.
  const standing =
    conversation === null
      ? null
      : [
          conversation.id,
          conversation.turns.length,
          conversation.turns.filter((turn) => turn.status === 'done').length,
          conversation.waitingOutlineTurnId,
          conversation.openQuestionsTurnId,
        ].join(':');
  useEffect(() => {
    if (standing !== null) void invalidateAiHistory(client);
  }, [standing, client]);

  const names = new Map((roster.data ?? []).map((row) => [row.id ?? 0, row.person?.fullName ?? '']));

  const state = ((): HistoryRailState => {
    if (statusRetry !== null) return { kind: 'failed', onRetry: statusRetry };
    if (history.isError && history.data === undefined) {
      return {
        kind: 'failed',
        onRetry: () => {
          void history.refetch();
        },
      };
    }
    if (history.data === undefined || (roster.isPending && !roster.isError)) return { kind: 'pending' };
    const entries = history.data.pages.flatMap((page) => page.items ?? []).map(historyEntryFromWire);
    return {
      kind: 'ready',
      groups: historyGroups(entries, now, timeZone),
      total: history.data.pages.at(-1)?.total ?? null,
      hasMore: history.hasNextPage,
      loadingMore: history.isFetchingNextPage,
      onMore: () => {
        if (!history.isFetchingNextPage) void history.fetchNextPage();
      },
    };
  })();

  return {
    state,
    clientName: (clientId) => {
      const name = names.get(clientId)?.trim() ?? '';
      return name === '' ? null : name;
    },
    now,
    timeZone,
    refresh: () => {
      void invalidateAiHistory(client);
    },
  };
}
